export const NUMBERED_HEADING = /^\d{1,2}[.)]\s+\S/
const INDENTED = /^(?:\t| {2,})/

const MIN_LINES = 3
const MIN_LONGEST_LINE = 60
export const WRAP_RATIO = 0.8
const ENDS_SENTENCE = /[.!?]$/

const startsCapitalised = (line: string) =>
  line.charAt(0) !== line.charAt(0).toLowerCase()

/**
 * Whether `line` carries on the paragraph that `previous` wrapped out of. A
 * short capitalised line after a finished sentence is a section title, not
 * the end of the paragraph. Both lines are expected trimmed.
 */
export const continuesWrappedLine = (
  previous: string,
  line: string,
  wrapWidth: number
) => {
  const startsShortSentence =
    ENDS_SENTENCE.test(previous) &&
    startsCapitalised(line) &&
    line.length < wrapWidth

  return (
    previous.length >= wrapWidth &&
    !NUMBERED_HEADING.test(previous) &&
    line !== '' &&
    !NUMBERED_HEADING.test(line) &&
    !startsShortSentence
  )
}

/**
 * PDF viewers put their text on the clipboard one visual line at a time, so a
 * wrapped paragraph arrives hard-broken mid-sentence. A line is treated as
 * wrapped when it fills most of the widest line in the paste - measuring
 * against the paste itself rather than a fixed width keeps this working for
 * documents with a narrower text measure.
 *
 * The clipboard carries no paragraph spacing, so an empty line is added after
 * each wrapped paragraph.
 *
 * Returns null when the paste does not look like wrapped prose, in which case
 * it should be left exactly as it came in.
 */
export function normalizePastedText(text: string): string | null {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')

  if (lines.length < MIN_LINES) return null
  if (lines.some(line => INDENTED.test(line))) return null

  const longest = Math.max(...lines.map(line => line.trimEnd().length))
  if (longest < MIN_LONGEST_LINE) return null

  const wrapWidth = longest * WRAP_RATIO
  const joined: { text: string; wrapped: boolean }[] = []

  lines.forEach((line, index) => {
    const previous = lines[index - 1]?.trim()
    const last = joined[joined.length - 1]
    const trimmed = line.trim()

    const continuesPrevious =
      last !== undefined &&
      previous !== undefined &&
      continuesWrappedLine(previous, trimmed, wrapWidth)

    if (continuesPrevious) {
      last.text += ` ${trimmed}`
      last.wrapped = true
    } else joined.push({ text: line.trimEnd(), wrapped: false })
  })

  return joined
    .flatMap(({ text }, index) => {
      const previous = joined[index - 1]
      const spaced = previous?.wrapped && previous.text !== '' && text !== ''
      return spaced ? ['', text] : [text]
    })
    .join('\n')
}
